import { percentageText } from "../utils/gridValueFormatters";
import {
  Circle as CircleIcon,
  Edit as EditIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  OpenInNewRounded as OpenLinkIcon,
} from "@mui/icons-material";
import { Box, Checkbox, Chip, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { parseDateValue } from "common/dates";
import React from "react";
import { Link } from "react-router-dom";

export const normalizeBooleanValue = (value) => {
  if (value === true || value === "true" || value === 1 || value === "1") {
    return true;
  }
  if (value === false || value === "false" || value === 0 || value === "0") {
    return false;
  }
  return null;
};

export const renderStatusIcon = (iconName, color) => {
  const iconProps = {
    sx: {
      fontSize: "1.25rem",
      color,
    },
  };
  switch (iconName) {
    case "circle":
      return <CircleIcon {...iconProps} />;
    case "edit":
      return <EditIcon {...iconProps} />;
    case "arrow_forward":
      return <ArrowForwardIcon {...iconProps} />;
    case "check_circle":
      return <CheckCircleIcon {...iconProps} />;
    case "cancel":
      return <CancelIcon {...iconProps} />;
    default:
      return null;
  }
};

const TargetPriceCellContent = ({ value, fractionDigitOptions }) => {
  const theme = useTheme();
  if (value == null) {
    return <React.Fragment />;
  }
  const delta = value - 100;
  const isGoodDeal = delta > 0;
  const isBadDeal = delta < 0;
  const formattedValue = Number.isFinite(value)
    ? percentageText(value, fractionDigitOptions)
    : value;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "flex-end",
        width: "100%",
        height: "100%",
      }}
    >
      <Box
        component={"span"}
        sx={{
          borderRadius: 2,
          px: "5px",
          py: "0",
          lineHeight: "1.4rem",
          ...(isGoodDeal && {
            backgroundColor: theme.brand.green[100],
            color: theme.brand.green[900],
          }),
          ...(isBadDeal && {
            backgroundColor: theme.brand.red[100],
            color: theme.brand.red[800],
          }),
        }}
      >
        {formattedValue}
      </Box>
    </Box>
  );
};

export const renderTargetPriceCell = (params, fractionDigitOptions) => (
  <TargetPriceCellContent
    value={params.value}
    fractionDigitOptions={fractionDigitOptions}
  />
);

export const renderBooleanCell = (params) => {
  const normalized = normalizeBooleanValue(params.value);
  if (normalized === null) {
    return <React.Fragment />;
  }
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
      }}
    >
      <Checkbox checked={normalized} disabled />
    </Box>
  );
};

const DEFAULT_ENUM_CHIP_COLOR = "text.secondary";

export const createEnumChipRenderer = (options, getEnumDisplayValue) => {
  const optionMap = new Map();
  if (Array.isArray(options)) {
    for (const opt of options) {
      optionMap.set(String(opt.internalName), opt);
    }
  }

  return (params) => {
    const rawValue = params.value;
    if (rawValue == null || rawValue === "") {
      return <React.Fragment />;
    }
    const key = String(rawValue);
    const opt = optionMap.get(key);
    const displayLabel =
      getEnumDisplayValue?.(rawValue) ?? opt?.displayName ?? key;
    const fg = opt?.foreground || DEFAULT_ENUM_CHIP_COLOR;
    const bg = opt?.background || null;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <Chip
          label={displayLabel}
          size={"small"}
          sx={{
            fontWeight: 500,
            color: fg,
            backgroundColor: bg || "transparent",
            ...(!bg && {
              border: `1px solid`,
              borderColor: fg,
            }),
          }}
        />
      </Box>
    );
  };
};

export const STATUS_OPTIONS = {
  DRAFT: {
    icon: "circle",
    color: "#f59e0b",
  },
  SHELVED: {
    icon: "edit",
    color: "#9ca3af",
  },
  QUOTE_REQUESTED: {
    icon: "arrow_forward",
    color: "#3b82f6",
  },
  DECLINED_TO_QUOTE: {
    icon: "edit",
    color: "#9ca3af",
  },
  QUOTED: {
    icon: "arrow_forward",
    color: "#3b82f6",
  },
  BOUND: {
    icon: "check_circle",
    color: "#10b981",
  },
  NOT_TAKEN_UP: {
    icon: "cancel",
    color: "#8b5cf6",
  },
  LOST: {
    icon: "cancel",
    color: "#8b5cf6",
  },
};
// Submission "open" link icon. Uses the same brand green as the Insured Name
// link (SubmissionNameLink) so submission links read consistently.
const SubmissionLinkIcon = ({ submissionId }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
      }}
    >
      <Tooltip title={`Open submission ${submissionId}`}>
        <Link
          to={`/submissions/${submissionId}`}
          target={"_blank"}
          style={{
            display: "flex",
            alignItems: "center",
            color: theme.brand.accent[600],
          }}
        >
          <OpenLinkIcon fontSize={"small"} />
        </Link>
      </Tooltip>
    </Box>
  );
};

const renderSubmissionIdCell = (params) => {
  const submissionId = params.value;
  if (!submissionId) return <React.Fragment />;
  return <SubmissionLinkIcon submissionId={submissionId} />;
};

export { renderSubmissionIdCell };

const SubmissionNameLink = ({ to, children }) => {
  const theme = useTheme();
  return (
    <Link
      to={to}
      target={"_blank"}
      rel={"noopener noreferrer"}
      style={{
        color: theme.brand.accent[600],
        textDecoration: "none",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Link>
  );
};

export const createSubmissionNameRenderer = (linkFieldName) => {
  return (params) => {
    const name = params.value;
    const linkValue = linkFieldName ? params.row[linkFieldName] : null;
    if ((name == null || name === "") && !linkValue) {
      return <React.Fragment />;
    }
    if (!linkValue) {
      return name;
    }
    return (
      <SubmissionNameLink to={`/submissions/${linkValue}`}>
        {name == null || name === "" ? (
          <OpenLinkIcon fontSize={"small"} />
        ) : (
          name
        )}
      </SubmissionNameLink>
    );
  };
};

export const getColumnTypeProps = (column) => {
  if (!column?._type) return {};
  switch (column._type) {
    case "INT":
    case "FLOAT":
      return {
        type: "number",
        align: "right",
        headerAlign: "right",
        cellClassName: (params) => {
          const { value } = params;
          const isText =
            value != null && typeof value === "string" && isNaN(Number(value));
          return isText
            ? "monospace tabular numeric-text-value"
            : "monospace tabular";
        },
      };
    case "BOOL":
      return {
        type: "boolean",
        align: "center",
        headerAlign: "center",
        valueGetter: (value) => normalizeBooleanValue(value),
        renderCell: renderBooleanCell,
      };
    case "DATE":
      return {
        type: "date",
        align: "left",
        headerAlign: "left",
        valueGetter: (value) => parseDateValue(value),
      };
    default:
      return {
        align: "left",
        headerAlign: "left",
      };
  }
};
